import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../config';
import WorldMap from '../components/WorldMap';

const SpeciesDetails = () => {
    const { speciesId } = useParams();
    const [species, setSpecies] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('general');

    useEffect(() => {
        const fetchSpecies = async () => {
            try {
                // If speciesId is numeric (from DB) or if we need to check both
                const response = await fetch(`${API_BASE_URL}/species/read.php?id=${speciesId}`);
                const data = await response.json();

                if (response.ok && data.records && data.records.length > 0) {
                    setSpecies(data.records[0]);
                } else {
                    // Fallback to static for now if not found in DB (helps during transition)
                    // but we should ideally just show not found if we want purely dynamic
                }
            } catch (error) {
                console.error('Error fetching species:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchSpecies();
    }, [speciesId]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    if (!species) {
        return <div className="text-center py-40 text-slate-500 font-serif italic text-2xl">Record not found in scientific archives</div>;
    }

    const tabs = [
        { id: 'general', label: 'Overview' },
        { id: 'taxonomy', label: 'Classification' },
        { id: 'images', label: 'Gallery' },
    ];

    return (
        <section className="min-h-screen bg-white font-sans selection:bg-emerald-100 selection:text-emerald-900">
            <div className="max-w-7xl mx-auto px-4 lg:px-12 pt-16 lg:pt-24 pb-12 lg:pb-20">
                {/* Header Block */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12 lg:mb-20">
                    <div className="lg:col-span-7 flex flex-col justify-center order-2 lg:order-1">

                        <div className="flex flex-wrap items-center gap-4 mb-6">
                            <span className="px-3 py-1 border border-emerald-900/10 rounded-full text-[10px] font-bold uppercase tracking-widest text-emerald-900/60 bg-emerald-50">
                                {species.category_id}
                            </span>
                            <span className="px-3 py-1 border border-emerald-900/10 rounded-full text-[10px] font-bold uppercase tracking-widest text-emerald-600 bg-white">
                                {species.family}
                            </span>
                        </div> 
                        <h2 className="text-xl lg:text-5xl font-serif italic text-slate-900 mb-8 border-l-2 border-emerald-500 pl-6 py-1">
                            {species.scientific_name}
                            <span className="text-slate-100 text-sm lg:text-lg not-italic ml-2 block sm:inline mt-1 sm:mt-0 font-sans  font-bold uppercase tracking-widest">{species.authorship}</span>
                        </h2>
                        <h1 className="text-4xl sm:text-5xl lg:text-3xl font-bold text-slate-400 mb-2 leading-tight font-display">
                            {species.common_name}
                        </h1>
                       

                        <p className="text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl font-medium font-sans italic opacity-80">
                            {species.description}
                        </p>
                    </div>

                    <div className="lg:col-span-5 order-1 lg:order-2">
                        <div className="relative aspect-[5/4] bg-slate-100 overflow-hidden">
                            <img src={species.images[0]} alt={species.scientificName} className="absolute inset-0 w-full h-full object-cover" />
                            {/* Decorative Frame */}
                            <div className="absolute inset-0 border-[1px] border-white/20 m-4 pointer-events-none"></div>
                        </div>
                        <p className="mt-3 text-[10px] text-slate-400 uppercase tracking-widest text-right">Fig 1.0 — Mature Specimen</p>
                    </div>
                </div>

                {/* Tabs */}
                <div className="mb-16 border-b border-slate-100 sticky top-16 bg-white z-40">
                    <div className="flex gap-8 md:gap-12 overflow-x-auto no-scrollbar pb-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    relative py-4 text-[10px] font-black uppercase tracking-[0.2em] transition-colors whitespace-nowrap font-display
                                    ${activeTab === tab.id ? 'text-emerald-900' : 'text-slate-400 hover:text-slate-600'}
                                `}
                            >
                                {tab.label}
                                {activeTab === tab.id && (
                                    <motion.div
                                        layoutId="activeTab"
                                        className="absolute bottom-0 left-0 right-0 h-[2px] bg-emerald-900"
                                    />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Content */}
                <div className="min-h-[400px]">
                    <AnimatePresence mode="wait">
                        {activeTab === 'general' && (
                            <motion.div
                                key="general"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16"
                            >
                                <div className="lg:col-span-4 space-y-8 lg:space-y-12">
                                    <div>
                                        <h3 className="text-xl font-serif text-slate-900 mb-4">Distribution Range</h3>
                                        <p className="text-slate-500 text-sm leading-relaxed mb-6">
                                            Recorded geographic distribution for {species.scientific_name}, highlighted by native status.
                                        </p>
                                        <ul className="space-y-4">
                                            <li className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 pb-4 border-b border-slate-50">
                                                <span className="w-24 shrink-0 text-xs font-bold uppercase tracking-widest text-emerald-700">Native</span>
                                                <span className="text-sm text-slate-600">{(species.distribution.native || []).join(", ")}</span>
                                            </li>
                                            {species.distribution.introduced && species.distribution.introduced.length > 0 && (
                                                <li className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                                                    <span className="w-24 shrink-0 text-xs font-bold uppercase tracking-widest text-slate-400">Introduced</span>
                                                    <span className="text-sm text-slate-600">{species.distribution.introduced.join(", ")}</span>
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                                <div className="lg:col-span-8 bg-slate-50 p-4 sm:p-8 lg:p-12 relative overflow-hidden rounded-xl h-[400px] sm:h-[500px]">
                                    <WorldMap
                                        nativeCountries={species.distribution.native || []}
                                        introducedCountries={species.distribution.introduced || []}
                                    />
                                    <div className="mt-6 flex gap-6 justify-center text-xs">
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 bg-emerald-600 rounded"></div>
                                            <span className="font-bold uppercase tracking-widest text-slate-600">Native</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <div className="w-4 h-4 bg-amber-500 rounded"></div>
                                            <span className="font-bold uppercase tracking-widest text-slate-600">Introduced</span>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'taxonomy' && (
                            <motion.div
                                key="taxonomy"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="max-w-4xl"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                                    <div>
                                        <h3 className="text-xl font-serif text-slate-900 mb-8 pb-4 border-b border-slate-100">Taxonomic Hierarchy</h3>
                                        <div className="space-y-0 relative border-l border-emerald-900/10 ml-2 pl-8 py-2">
                                            {Object.entries(species.classification).map(([rank, name], i) => (
                                                <div key={rank} className="relative mb-8 last:mb-0">
                                                    <span className="absolute -left-[37px] top-2 w-2 h-2 rounded-full border-2 border-white bg-emerald-200"></span>
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{rank}</p>
                                                    <p className={`text-lg font-serif ${i === Object.keys(species.classification).length - 1 ? 'text-emerald-800 font-bold' : 'text-slate-700'}`}>{name}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-serif text-slate-900 mb-8 pb-4 border-b border-slate-100">Synonyms</h3>
                                        <ul className="space-y-6">
                                            {species.synonyms.map((syn, idx) => (
                                                <li key={idx}>
                                                    <p className="text-lg font-serif italic text-slate-600">{syn.name}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-xs text-slate-400 font-mono">{syn.year}</span>
                                                        <span className="w-px h-3 bg-slate-200"></span>
                                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{syn.type || 'Homo.'}</span>
                                                    </div>
                                                </li>
                                            ))}
                                            {species.synonyms.length === 0 && <li className="text-slate-400 italic">No synonyms recorded.</li>}
                                        </ul>
                                    </div>
                                </div>
                            </motion.div>
                        )}

                        {activeTab === 'images' && (
                            <motion.div
                                key="images"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="grid grid-cols-1 md:grid-cols-3 gap-1"
                            >
                                {species.images.map((img, index) => (
                                    <div key={index} className="aspect-square relative group overflow-hidden bg-slate-100">
                                        <img src={img} alt="" className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                                        <div className="absolute inset-0 bg-emerald-900/0 group-hover:bg-emerald-900/20 transition-colors duration-500"></div>
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                                            <span className="bg-white/90 backdrop-blur text-emerald-900 px-6 py-3 text-[10px] font-bold uppercase tracking-widest">
                                                Enlarge
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

export default SpeciesDetails;
